import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PlayerIDForm from '../playerIDForm';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ChampionshipTable from './ChampionshipTable';
import { Person } from "@mui/icons-material";

export default function Profile() {
  const { userData, logout } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  // Auto-dismiss error toast after 1 second
  useEffect(() => {
    if (!error) return;
    const timeoutId = setTimeout(() => setError(null), 1000);
    return () => clearTimeout(timeoutId);
  }, [error]);

  const fetchData = useCallback(async () => {
    if (!userData?.userID) return;

    try {
      setLoading(true);
      const { data: resData } = await axios.get(`${process.env.REACT_APP_MINF_LATEST_BAC_BASE}/api/users/${userData.userID}/creators-count`);

      const countData = (resData.creators || []).reduce((acc, item) => {
        acc[item.class] = (acc[item.class] || 0) + 1;
        return acc;
      }, {});

      setCounts(countData);
      setData(resData.creators || []);
    } catch (err) {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [userData?.userID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statsData = useMemo(() => [
    {
      label: 'Fighters',
      value: (counts.FN || 0) + (counts.FC || 0),
      icon: <Person />,
      color: 'from-blue-500 to-purple-500'
    },
    {
      label: 'T. H.',
      value: counts.T || 0,
      icon: <Person />,
      color: 'from-amber-500 to-orange-500'
    },
    {
      label: 'Guests',
      value: counts.G || 0,
      icon: <Person />,
      color: 'from-emerald-500 to-teal-500'
    }
  ], [counts]);

  if (!userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">User not authenticated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <p className="text-gray-600 mt-4 text-center">Loading your profile...</p>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-8 py-4 rounded-2xl shadow-2xl z-50 animate-fade-in">
          <div className="flex items-center space-x-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative h-[450px] md:h-96 w-[97%] mx-auto rounded-2xl overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://cdn.pixabay.com/photo/2015/02/06/01/14/wallpaper-625623_1280.png')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60"></div>
        </div>

        {/* Profile Card */}
        <div className="relative h-full flex items-end justify-center pb-8">
          <div className="w-full max-w-4xl mx-auto px-4">
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
              {/* Profile Header */}
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
                  <div className="relative">
                    <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden ring-4 ring-white shadow-2xl">
                      <img
                        src={userData?.photo || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80"}
                        alt="User profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>

                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{userData.name}</h3>
                    <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-3">
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg">
                        {userData.role || 'Athlete'}
                      </span>
                      <span className="text-gray-500 text-sm">• Active Member</span>
                    </div>
                  </div>

                  <button
                    onClick={logout}
                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="px-4 md:px-6 mt-12 pb-12">
        <div className="max-w-6xl mx-auto space-y-6 md:space-y-8">
          {/* Stats Cards */}
          <div className="w-full max-w-md md:max-w-lg mx-auto grid grid-cols-3 gap-2 md:gap-3">
            {statsData.map((stat) => (
              <div
                key={stat.label}
                className="relative bg-white rounded-lg px-3 py-2.5 border border-gray-100 shadow-sm hover:shadow-md transition-all"
              >
                <span className={`absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b ${stat.color} rounded-l-lg`} />
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-base md:text-lg font-semibold text-gray-900 leading-5 truncate">{stat.value}</p>
                    <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wide truncate">{stat.label}</p>
                  </div>
                  <div className={`shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-md bg-gradient-to-br ${stat.color} flex items-center justify-center text-xs md:text-sm text-white/95`}> 
                    {stat.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Championship Table */}
          <div className="max-w-7xl mx-auto">
            <ChampionshipTable title="" items={data} userfc={userData} />
          </div>
        </div>
      </div>

      {/* Player ID Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 md:p-8">
            <PlayerIDForm onClose={() => setShowForm(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
